import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
    DataServiceError,
    DefaultDataServiceConfig,
    EntitySelectors,
    EntitySelectorsFactory,
    HttpUrlGenerator
} from '@ngrx/data';
import { Update } from '@ngrx/entity';
import { select, Store } from '@ngrx/store';
import { Observable, throwError } from 'rxjs';
import { catchError, map, withLatestFrom } from 'rxjs/operators';
import { IGenericResponse, ResponseStatus } from '../../common';
import { LoggingService } from '../../shared/services/logging/logging.service';
import { EfaactDynamicDataService } from '../efaact.dynamic-data-service';
import { BudgetWorksheet } from './rate-planning.model';
import * as fromShared from '../../shared';

export const entityName = 'IndirectRatePlanning';

@Injectable()
export class RatePlanningDataService extends EfaactDynamicDataService<BudgetWorksheet> {

    selectors: EntitySelectors<BudgetWorksheet>;
    constructor(
        http: HttpClient,
        httpUrlGenerator: HttpUrlGenerator,
        log: LoggingService,
        config: DefaultDataServiceConfig,
        store: Store
    ) {
        super(http, httpUrlGenerator, entityName, log, config, store);
        this.selectors = new EntitySelectorsFactory().create<BudgetWorksheet>(
            this.entityName
        );
    }

    protected save(update: Update<any>): Observable<any> {
        this.log.verbose({
            type: `${this.entityName}DataService(EfaactDataService):save`,
            update
        });
        const item = {
            ...update.changes
        } as any;
        delete item.original;
        return this.http
            .put<IGenericResponse<any>>(this.entitiesUrl, {
                [`${this.entityName}List`]: [item]
            })
            .pipe(
                withLatestFrom(
                    this.store.pipe(select(this.selectors.selectEntities))
                ),
                map(([resp, items]) => {
                    const wksht = items.find(
                        x => x.CostType == resp.Items[0].CostType
                    );
                    let line = wksht.Line.find(
                        x => x.Month == resp.Items[0].Month
                    );
                    line = {
                        ...line,
                        Budget: resp.Items[0].Budget,
                        Forecast: resp.Items[0].Forecast
                    };
                    this.log.verbose({
                        type: `${this.entityName}DataService(EfaactDataService):save:resp`,
                        resp
                    });
                    if (!fromShared.Errors.IsSuccessStatus(resp.StatusCode)) {
                        this.store.dispatch(
                            new fromShared.Errors.SetErrors(
                                fromShared.Errors.MapErrorsToStatus({
                                    ...resp,
                                    Items: resp.Items.filter(x => !!x).map(
                                        x => ({
                                            ...x,
                                            Status: {
                                                ...x.Status,
                                                MsgEntityID:
                                                    update.changes.original &&
                                                    update.changes.original.id
                                            }
                                        })
                                    )
                                })
                            )
                        );
                        throwError(
                            new DataServiceError(
                                fromShared.Errors.getResponseErrors(
                                    resp
                                ).reduce(
                                    (a, c, i) => (i === 0 ? c : `${a}\n${c}`),
                                    ''
                                ),
                                { method: 'PUT', url: this.entitiesUrl }
                            )
                        );

                        return {
                            ...update.changes,
                            ...update.changes.original,
                            ...resp.Items[0],
                            Status:
                                resp.Items[0] && resp.Items[0].Status
                                    ? resp.Items[0].Status
                                    : {
                                          StatusCode: resp.StatusCode,
                                          Message: resp.StatusType
                                      }
                        } as BudgetWorksheet;
                    }
                    if (
                        resp.StatusCode === ResponseStatus.Warning &&
                        resp.Items.some(x => !x.Status.MsgEntityID)
                    ) {
                        this.log.verbose({
                            type: `${this.entityName}DataService(EfaactDataService):save:ResponseStatus.Warning`,
                            resp
                        });
                        this.store.dispatch(
                            new fromShared.Errors.SetErrors(
                                fromShared.Errors.MapErrorsToStatus({
                                    ...resp,
                                    Items: resp.Items.filter(
                                        x =>
                                            !!x &&
                                            x.Status &&
                                            !x.Status.MsgEntityID
                                    )
                                })
                            )
                        );
                    }
                    const index = wksht.Line.findIndex(x => x.Month == resp.Items[0].Month);
                    const wkshtLines = wksht.Line.map(line => ({
                        Month: line.Month,
                        Budget: line.Budget,
                        Forecast: line.Forecast
                    }));
                    wkshtLines.splice(index, 1);
                    wkshtLines.splice(index, 0, line);
                    const ret = {
                        ...wksht,
                        Line: wkshtLines,
                        Status: {
                            StatusCode: ResponseStatus.Success,
                            Message: 'Saved'
                        }
                    } as BudgetWorksheet;
                    this.log.verbose({
                        type: `${this.entityName}DataService(RatePlanningDataService):save:return`,
                        ret,
                        update,
                        resp: resp.Items
                    });
                    return ret;
                }),
                catchError((e, c) =>
                    fromShared.Errors.handleDataServiceError(this.store, e, c)
                )
            );
    }

    mapItems(item: BudgetWorksheet): BudgetWorksheet {
        if (!item) {
            return;
        }
        return {
            ...item,
            id: `${item.CostType}-${item.CCID}-${item.Line == null ? '' : item.Line[0].CalcType}`
        };
    }
}
